import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HubioLoginService } from '@hubio/hubio-login';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { allowedUrl, toFrame, toPage, V11Service } from './v11-admin';

const BOUNCED = 'user-management-bounced';
const FRAME_STYLE = 'r8ui-user-management';

// v11's admin, a separate app under /v11 on hosts that serve both, framed under
// r8ui's own header and nav (v11 drops its own when framed). Each v11 admin page
// keeps an r8ui URL of its own (see toFrame/toPage), so it can be reloaded and linked.
@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent implements OnInit, OnDestroy {
  @ViewChild('frame') frame: ElementRef<HTMLIFrameElement>;
  public src: SafeResourceUrl;
  public failed = false;
  public direct: string;
  private navigations: Subscription;
  private groups: string[];

  constructor(private router: Router, private sanitizer: DomSanitizer, private v11: V11Service, private zone: NgZone,
              private authService: HubioLoginService) {}

  ngOnInit() {
    this.groups = this.authService.getUserGroups();
    // The route admits only groups that open some page, but not necessarily this one
    const url = allowedUrl(this.router.url, this.groups);
    if (!url) {
      return;
    }
    if (url !== this.router.url) {
      this.router.navigateByUrl(url, { replaceUrl: true });
    }
    this.direct = toFrame(url);
    // The frame needs the ALB session /v11 sits behind, and Cognito refuses to be
    // framed, so a user without one goes through /v11 at the top level first: a
    // silent login (they hold a Cognito session from signing in here) that v11
    // answers by sending them back. Once only, so a failure can't loop.
    this.v11.status().then(status => {
      if (status === 'signed-out' && !sessionStorage.getItem(BOUNCED)) {
        sessionStorage.setItem(BOUNCED, '1');
        this.leave('/v11/?return=' + encodeURIComponent(url));
        return;
      }
      sessionStorage.removeItem(BOUNCED);
      if (status === 'signed-in') {
        // A path built from r8ui's own route, always under /v11/admin
        this.src = this.sanitizer.bypassSecurityTrustResourceUrl(this.direct);
      } else {
        this.failed = true;
      }
    }, () => this.failed = true);

    // r8ui navigating between its own /user-management pages (the header link) moves the frame
    this.navigations = this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
      const allowed = allowedUrl(this.router.url, this.groups);
      if (allowed !== this.router.url) {
        this.router.navigateByUrl(allowed, { replaceUrl: true });
        return;
      }
      const win = this.frameWindow();
      const want = toFrame(this.router.url);
      if (win && win.location.pathname + win.location.search !== want) {
        win.location.replace(want);
      }
    });
  }

  ngOnDestroy() {
    if (this.navigations) {
      this.navigations.unsubscribe();
    }
  }

  // Each document the frame loads: follow its page changes, which v11 makes with
  // pushState, so the address bar names the page shown. Same origin, so the frame's
  // own Navigation API reports them; where it is missing only full loads are seen.
  onLoad() {
    const win = this.frameWindow();
    if (!win) {
      return;
    }
    this.underHeader(win.document);
    this.syncUrl();
    const navigation = (win as any).navigation;
    if (navigation) {
      navigation.addEventListener('currententrychange', () => this.zone.run(() => this.syncUrl()));
    }
  }

  leave(url: string) {
    window.location.href = url;
  }

  retry() {
    sessionStorage.removeItem(BOUNCED);
    window.location.reload();
  }

  // Replace, not push: the frame's own navigation already made the history entry
  private syncUrl() {
    const win = this.frameWindow();
    const page = win && toPage(win.location.pathname + win.location.search);
    const allowed = page && allowedUrl(page, this.groups);
    if (allowed !== page) {
      win.location.replace(toFrame(allowed));
    } else if (page && page !== this.router.url) {
      this.router.navigateByUrl(page, { replaceUrl: true });
    }
  }

  // r8ui's header carries v11's tabs, so hide the tab bar v11 draws when framed and
  // start its page just under the header. Kept here, not in v11, so v11 stays as is;
  // if v11's markup changes, its tab bar merely shows again.
  private underHeader(doc: Document) {
    if (doc.getElementById(FRAME_STYLE)) {
      return;
    }
    const style = doc.createElement('style');
    style.id = FRAME_STYLE;
    style.textContent = 'main.framed > .mantine-Tabs-root { display: none !important; }'
      + ' main.framed { padding-top: 16px !important; }';
    doc.head.appendChild(style);
  }

  private frameWindow(): Window {
    return this.frame && this.frame.nativeElement.contentWindow;
  }
}
